import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  indexArrayByPropertyName,
  sortByProperties,
} from 'donor-elf-web/lib/array-utils';
import CampaignPledgeModel from 'donor-elf-web/models/campaign-pledge';
import ContactModel from 'donor-elf-web/models/contact';
import PledgeModel from 'donor-elf-web/models/pledge';
import AbilitiesService from 'donor-elf-web/services/abilities';
import AddSomethingModal, {
  type PledgeSavedArgs,
} from 'donor-elf-web/services/add-something-modal';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import Settings from 'donor-elf-web/services/settings';

interface ComponentArgs {
  contact: ContactModel;
  pledges: PledgeModel[];
  campaignPledges?: CampaignPledgeModel[];
}

interface Item {
  pledge: PledgeModel;
  campaignPledge?: CampaignPledgeModel;
  hasNotesOrCampaignPledge: boolean;
}

export default class PledgesComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);

    this.hasManualAndNotManualPledges =
      this.args.pledges.some((x) => x.isManual) &&
      this.args.pledges.some((x) => !x.isManual);
  }

  @service declare abilities: AbilitiesService;
  @service declare addSomethingModal: AddSomethingModal;
  @service declare fundGoalService: FundGoalService;
  @service declare settings: Settings;

  @tracked addedPledges: PledgeModel[] = [];
  @tracked deletedPledges: PledgeModel[] = [];
  @tracked addedCampaignPledges: CampaignPledgeModel[] = [];
  @tracked deletedCampaignPledges: CampaignPledgeModel[] = [];
  savePledgeSubsciptionToken?: string;

  hasManualAndNotManualPledges: boolean;

  get showCard() {
    return this.sortedItems.length > 0 || this.deletedPledges.length > 0;
  }

  get hasYtdOrTotalGoalType() {
    return (
      this.fundGoalService.hasYtdGoalType ||
      this.fundGoalService.hasTotalOrTotalAndExpectingGoalType
    );
  }

  get campaignPledgesByPledgeId() {
    const { addedCampaignPledges, deletedCampaignPledges } = this;
    const currentCampaignPledges = [
      ...(this.args.campaignPledges || []),
      ...addedCampaignPledges,
    ].filter((x) => !deletedCampaignPledges.includes(x));
    return indexArrayByPropertyName(currentCampaignPledges, 'pledgeId');
  }

  @cached
  get sortedItems(): Item[] {
    let { campaignPledgesByPledgeId } = this;
    let items = [
      ...this.args.pledges,
      ...this.addedPledges.filter((x) => x.contactId === this.args.contact.id),
    ]
      .filter((x) => !this.deletedPledges.includes(x))
      .map((pledge) => {
        let campaignPledge = campaignPledgesByPledgeId[pledge.id];
        return {
          pledge,
          campaignPledge,
          hasNotesOrCampaignPledge:
            isPresent(pledge.notes) ||
            campaignPledgesByPledgeId[pledge.id] != null,
        };
      });

    return sortByProperties(items, ['pledge.startDate:desc']);
  }

  get showAnnualAmount() {
    return (
      this.settings.userSettings!.showAnnualGoal ||
      this.fundGoalService.hasYtdGoalType
    );
  }

  templateDidInsert = () => {
    if (this.savePledgeSubsciptionToken == null) {
      this.savePledgeSubsciptionToken =
        this.addSomethingModal.subscribeToPledgeSaved(
          this.handleAddSomethingPledgeSaved,
        );
    }
  }

  templateWillDestroy = () => {
    if (this.savePledgeSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.savePledgeSubsciptionToken,
      );
    }
  }

  handleAddSomethingPledgeSaved = async (_msg: string, data: PledgeSavedArgs) => {
    if (data.newPledge) {
      this.addedPledges = [...this.addedPledges, data.pledge];
      if (data.campaignPledge) {
        this.addedCampaignPledges = [
          ...this.addedCampaignPledges,
          data.campaignPledge,
        ];
      }
    } else if (data.deleted) {
      this.deletedPledges = [...this.deletedPledges, data.pledge];
    } else {
      let existingItem = this.sortedItems.find(
        (x) => x.pledge.id === data.pledge.id,
      );
      if (existingItem?.campaignPledge?.id !== data.campaignPledge?.id) {
        if (data.campaignPledge) {
          // add the new campaign pledge
          this.addedCampaignPledges = [
            ...this.addedCampaignPledges,
            data.campaignPledge,
          ];
        }
        if (existingItem?.campaignPledge) {
          // remove the deleted campaign pledge
          this.deletedCampaignPledges = [
            ...this.deletedCampaignPledges,
            existingItem.campaignPledge,
          ];
        }
      }
    }
  }
}
